import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Popup from './popup';
import './styles/quote.css';

const Quotation = () => {
  const [formData, setFormData] = useState({
    email: '',
    product: '',
    quantity: '',
    name: '',
    company: '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: '', message: '' });
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Clear alert after 12.5 seconds
  useEffect(() => {
    let timeoutId;
    if (alert.show) {
      timeoutId = setTimeout(() => {
        setAlert({ show: false, type: '', message: '' });
      }, 12500);
    }
    return () => clearTimeout(timeoutId);
  }, [alert.show]);

  const showNotification = (type, message) => {
    // Show both popup and alert
    setPopup({
      show: true,
      type,
      message
    });
    setAlert({
      show: true,
      type,
      message
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Basic validation
    if (!formData.email) {
      showNotification('error', 'Please provide your email address');
      setIsSubmitting(false);
      return;
    }

    try {
      const templateParams = {
        to_email: formData.email,
        from_name: formData.name || 'Not provided',
        company: formData.company || 'Not provided',
        product: formData.product || 'Not specified',
        quantity: formData.quantity || 'Not specified',
        message: formData.comments || 'None',
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID_QUOTE,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      // Reset form
      setFormData({
        email: '',
        product: '',
        quantity: '',
        name: '',
        company: '',
        comments: ''
      });
      
      showNotification(
        'success',
        'Thank you for requesting a quote. One of our experts will reach out to you via email within 24 hours.'
      );
      
    } catch (error) {
      showNotification('error', 'Failed to send email. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <div className="quotation-container">
      {popup.show && (
        <Popup
          type={popup.type}
          message={popup.message}
          onClose={() => setPopup({ show: false, type: '', message: '' })}
          duration={10000} // Set popup duration to 10 seconds to match alert
        />
      )}
      <div className="quote-form-section">
        {alert.show && (
          <div className={`alert ${alert.type === 'success' ? 'alert-success' : 'alert-error'}`}>
            <h4>{alert.type === 'success' ? 'Success!' : 'Error!'}</h4>
            <p>{alert.message}</p>
          </div>
        )}

        <div className="quote-header">
          <h1>Get Your Quote Now</h1>
          <p className="subheader">Fill out this form for a personalized quote.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address<span className="required">*</span></label>
            <small>This is where we'll send your quote.</small>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="product">Product Interested In</label>
            <small>Let us know which product you're eyeing, or just say "Not Sure" if you need guidance.</small>
            <input
              type="text"
              id="product"
              name="product"
              value={formData.product}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="quantity">Quantity</label>
            <small>How many units are you considering?</small>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">Your Name</label>
            <small>Personalize your quote even further.</small>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Company/Clinic Name</label>
            <small>Helps us understand your organizational needs better.</small>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="comments">Additional Comments</label>
            <small>Any specific requirements or questions?</small>
            <textarea
              id="comments"
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form-footer">
            <p className="privacy-notice">
              We prioritize your privacy. Your information is safeguarded with us. View our 
              <Link to="#" className="privacy-link">Privacy Policy</Link>
            </p>
            
            <button 
              type="submit" 
              className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Get My Quote'}
            </button>
          </div>
        </form>

        <div className="additional-info">
          <p className="info-item">
            If you have any questions in the meantime, feel free to{' '}
            <Link to="/contact" className="contact-link">contact us</Link> directly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Quotation;